import {
  ArrayField,
  AutocompleteArrayInput,
  AutocompleteInput,
  BooleanField,
  BooleanInput,
  ChipField,
  Create,
  Datagrid,
  DateField,
  Edit,
  EditButton,
  FormDataConsumer,
  FormTab,
  ImageField,
  List,
  ListButton,
  minLength,
  NumberInput,
  ReferenceArrayInput,
  ReferenceField,
  ReferenceInput,
  required,
  Show,
  SimpleForm,
  SimpleShowLayout,
  SingleFieldList,
  Tab,
  TabbedForm,
  TabbedShowLayout,
  TextField,
  TextInput,
  TopToolbar,
  useCreate,
  useMutation,
  useNotify,
  useRedirect,
  useRefresh,
  ResourceContextProvider,
  DeleteButton
} from "react-admin";
import { ShowSplitter } from 'ra-compact-ui';
import React from "react";
import { Card as MuiCard, Chip, makeStyles, withStyles } from '@material-ui/core';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import { gql, useMutation as apolloUseMutation } from '@apollo/react-hooks';


const Card = withStyles(theme => ({
  root: {
    [theme.breakpoints.up('sm')]: {
      order: -1, // display on the left rather than on the right of the list
      width: '15em',
      marginRight: '1em',
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
}))(MuiCard);

const companySearch = (n) => {
  if (n && n.length > 2) {
    return {
      name__like: n,
    }
  }
}

const drawingSearch = (n) => {
  if (n && n.length >= 2) {
    return {
      display_name__like: n,
    }
  }
}

const accessLevelChoices = [{ id: 0, name: "No access" }, { id: 1, name: "View Only" }, { id: 2, name: "Edit" }, {
  id: 3,
  name: "Admin"
}];

const useQuickFilterStyles = makeStyles(theme => ({
  chip: {
    marginBottom: theme.spacing(1),
  },
}));

const QuickFilter = ({ label }) => {
  const classes = useQuickFilterStyles();
  return <Chip className={classes.chip} label={label}/>;
};

const groupFilters = [
  <NumberInput label="id" source="id" alwaysOn/>,
  <TextInput label="Name" source="name__like" alwaysOn/>,
  <ReferenceInput label="Project" source="project_id" reference="Project" filterToQuery={companySearch} alwaysOn>
    <AutocompleteInput source="name"/>
  </ReferenceInput>,
  <ReferenceInput label="Company" source="company.id" reference="Company" filterToQuery={companySearch} alwaysOn>
    <AutocompleteInput label="Comp" source="name"/>
  </ReferenceInput>,
];

export const GroupList = props => (
  <List {...props} filters={groupFilters}>
    <Datagrid rowClick="show">
      <TextField source="id"/>
      <TextField source="name"/>
      <ReferenceField label="Project" source={"project.id"} reference={'Project'} sortable={false} link="show">
        <TextField source="name"/>
      </ReferenceField>
      <BooleanField source="is_default" looseValue={true}/>
      <ArrayField source="companies">
        <SingleFieldList>
          <ReferenceField reference='Company' source={'id'} link={'show'}>
            <ChipField source="name"/>
          </ReferenceField>
        </SingleFieldList>
      </ArrayField>
    </Datagrid>
  </List>
);

const PostViewActions = ({ basePath, data, resource }) => (
  <TopToolbar>
    <ListButton basePath={basePath} label="Back" icon={<ChevronLeft/>}/>
    <EditButton basePath={basePath} record={data} link={'edit'}/>
    <DeleteButton basePath={basePath} record={data} resource={resource} />
  </TopToolbar>
);
export const GroupView = props => (
  <Show {...props} actions={<PostViewActions/>}>
    <TabbedShowLayout syncWithLocation={false}>
      <Tab label="summary">
        <ShowSplitter
          leftSideProps={{
            md: 4,
            component: 'div'
          }}
          rightSideProps={{
            md: 8
          }}
          leftSide={(
            <SimpleShowLayout>
              <TextField source="id"/>
              <TextField source="name"/>
              <ReferenceField label="Project" source={"project.id"} reference={'Project'} sortable={false}
                              link="show">
                <TextField source="name"/>
              </ReferenceField>
              <BooleanField source="is_default" looseValue={true}/>
              <ArrayField source="companies">
                <SingleFieldList>
                  <ReferenceField reference='Company' source={'id'} link={'show'}>
                    <ChipField source="name"/>
                  </ReferenceField>
                </SingleFieldList>
              </ArrayField>
            </SimpleShowLayout>)}
          rightSide={(
            <SimpleShowLayout>
              <ArrayField source="recordTypes">
                <SingleFieldList link={false}>
                  <ReferenceField reference='RecordType' source={'id'} link={false}>
                    <ChipField source="name"/>
                  </ReferenceField>
                </SingleFieldList>
              </ArrayField>
            </SimpleShowLayout>
          )}
        />
      </Tab>
      <Tab label="Drawings">
        <ArrayField source="drawings">
          <Datagrid>
            <ReferenceField reference='Drawing' source={'id'} link={false}>
              <ChipField source="display_name"/>
            </ReferenceField>
            <ReferenceField reference='Drawing' source={'id'} link="show" label="image">
              <ImageField source="url"/>
            </ReferenceField>
            <ReferenceField reference='Drawing' source={'id'} link={false} label="Created At">
              <DateField source="created_at"/>
            </ReferenceField>
            <ReferenceField reference='Drawing' source={'id'} link={false} label="Updated At">
              <DateField source="updated_at"/>
            </ReferenceField>
          </Datagrid>
        </ArrayField>
      </Tab>
    </TabbedShowLayout>
  </Show>
)

export const GroupCreate = props => {
  const [create, { data }] = useCreate('Group');
  const redirectTo = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();

  const handleSubmit = async (values, redirect) => {
    await create({ payload: { data: { createGroupInput: values } } }, {
      onSuccess: ({ data: newRecord }) => {
        notify(`Changes saved`)
        refresh();
        redirectTo(redirect, props.basePath, newRecord.id, newRecord);
      }
    });
  }

  return (
    <Create title='Create Workflow Rule' {...props}>
      <SimpleForm redirect="show" save={handleSubmit}>
        <ReferenceInput label="Project" source="project_id" reference="Project" filterToQuery={companySearch}
                        validate={required()}>
          <AutocompleteInput optionText="name" source={"id"} validate={required()}/>
        </ReferenceInput>
        <TextInput source="name" validate={[required(), minLength(3)]}/>
        <BooleanInput source={'all_record_types'} initialValue={true}/>
        <BooleanInput source={'all_drawings'} initialValue={true}/>
      </SimpleForm>
    </Create>
  );
}


export const GroupEdit = props => {

  const [mutate] = useMutation();
  const [setGroupCompanies] = apolloUseMutation(gql`
      mutation SetCompaniesInGroup($groupId: Int!, $companyIds: [Int!]!) {
          setCompaniesInGroup(groupId: $groupId, companyIds: $companyIds) {
              id
              companies {
                  id
                  name
                  __typename
              }
              __typename
          }
      }`)
  const [setGroupDrawings] = apolloUseMutation(gql`
      mutation SetDrawingsInGroup($groupId: Int!, $drawingIds: [Int!]!) {
          setDrawingsInGroup(groupId: $groupId, drawingIds: $drawingIds) {
              id
              drawings {
                  id
                  __typename
              }
              __typename
          }
      }
  `)
  const [updateGroup] = apolloUseMutation(gql`
    mutation UpdateGroup($id: Int!, $updateGroupInput: UpdateGroupInput!) {
      updateGroup(id: $id, updateGroupInput:$updateGroupInput) {
          id
          is_default
        }
    }
  `)
  const redirectTo = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();

  const handleSubmit = async (values, redirect) => {
    const { id, name, is_default } = values;
    await updateGroup({ variables: { id: id, updateGroupInput:{ is_default:Number(is_default), name } } });
    await setGroupCompanies({ variables: { groupId: id, companyIds: values.companiesIds } });
    await setGroupDrawings({ variables: { groupId: id, drawingIds: values.drawingsIds || [] } });
    notify(`Changes saved`)
    refresh();
  }

  return (
    <Edit title='Create Workflow Rule' {...props}>
      <TabbedForm redirect="show" save={handleSubmit}>
        <FormTab label='General'>
          <ReferenceField label="Project" source="project.id" reference="Project" link={false}>
            <TextField optionText="name" source={"name"}/>
          </ReferenceField>
          <TextInput source="name" validate={[required(), minLength(3)]}/>
          <BooleanInput source={'is_default'}/>
          <FormDataConsumer>
            {(data, ...rest) => {
              return (
                <ReferenceArrayInput source="companiesIds" reference="Company" resource="Company"
                                     filter={{ project_id: data.record.project.id }} filterToQuery={companySearch}>
                  <AutocompleteArrayInput optionText="name"/>
                </ReferenceArrayInput>
              );
            }}
          </FormDataConsumer>
        </FormTab>
        <FormTab label='Drawing'>
          <FormDataConsumer>
            {(data, ...rest) => {
              return (
                <ReferenceArrayInput source="drawingsIds" reference="Drawing" resource="Drawing"
                                     filter={{ project_id: data.record.project.id }} filterToQuery={drawingSearch} sort={{field: "display_name"}} perPage={1000}>

                  <AutocompleteArrayInput optionText="display_name"/>
                </ReferenceArrayInput>
              );
            }}
          </FormDataConsumer>
        </FormTab>
      </TabbedForm>
    </Edit>
  );
}

